// Pure capture-guidance logic — the on-device foot/paper detection ported
// faithfully from the archived in-house capture prototype
// (`reference/scanner/index.html`). Everything here is ort-free and
// deterministic so it unit-tests without loading the wasm runtime: tensor
// pre-processing, the float16<->float32 bit-twiddle, the 4-class argmax,
// connected-component cleanup, bounding boxes, and the guidance-string rules.
//
// The ONNX session wrapper (which DOES import onnxruntime-web) lives in
// `guidance-session.ts`; CaptureFlow dynamic-imports that at camera-start so
// the heavy wasm module never enters the static graph (keeps tests hermetic).
//
// Model contract (from the prototype): 128×128 RGB input, ImageNet-normalised,
// NCHW planar, float16 → 4-class segmentation logits `[1,4,128,128]`
// (bg / foot-left / foot-right / paper), possibly float16 out.

import type { Side } from "./capture";

/** Model input/output spatial size — the prototype's fixed 128×128 crop. */
export const MODEL_SIZE = 128;
/** Segmentation classes emitted by the model. */
export const NUM_CLASSES = 4;
const PIXELS = MODEL_SIZE * MODEL_SIZE; // 16384

// Class ids in the model's output. 1 = left-foot type, 2 = right-foot type,
// 3 = paper, 0 = background. After wrong-foot detection the two foot classes
// collapse into `CLASS_FOOT` so the downstream geometry is side-agnostic.
export const CLASS_FOOT = 1;
export const CLASS_FOOT_OTHER = 2;
export const CLASS_PAPER = 3;

// ImageNet normalisation — the exact constants the prototype's preprocessor used.
export const IMAGENET_MEAN = [0.485, 0.456, 0.406] as const;
export const IMAGENET_STD = [0.229, 0.224, 0.225] as const;

// Paper-bbox guidance thresholds, expressed as fractions of the 128px frame
// (prototype constants: 0.9 / 0.5 / 0.05 / 0.1).
export const PAPER_TOO_LARGE_FRAC = 0.9; // → "TOO CLOSE, MOVE BACK"
export const PAPER_TOO_SMALL_FRAC = 0.5; // → "TOO FAR AWAY, MOVE CLOSER"
export const PAPER_EDGE_FRAC = 0.05; // → "PAPER TOO CLOSE TO EDGE"
export const PAPER_MIN_WIDTH_FRAC = 0.1; // discard a paper bbox thinner than this

// Camera-angle window per photo (deg, keyed by `CaptureStep.photoIndex`). The
// angle comes from the phone's deviceorientation, NOT the mask (see
// `angleFromBeta`): 0° looks straight ahead, 90° straight down. The windows are
// the old IBV iOS app's (`CameraScreenVC.cameraAngle`), which counted from the
// other end: 30-50° tilt for the side photos 1/3 and 10-20° for photo 2, so
// photo 2 is almost a top view. Since IBV gets the same shots as our solver,
// every scan uses them; footscan-server experiments/README.md, section "Foto 2
// bei 10-20°", measured that our solver loses nothing against 40-50°.
export const ANGLE_WINDOWS: Readonly<Record<1 | 2 | 3, readonly [number, number]>> = {
  1: [40, 60],
  2: [70, 80],
  3: [40, 60],
};

// Lens-occlusion thresholds (0–255 luma). A finger over the rear lens — easy to
// do on the phone grips this flow encourages — yields a near-black or flat
// dark-brown frame the segmentation model happily runs on, so without this
// check the customer just sees "NO FOOT DETECTED" and never thinks of the lens.
export const OCCLUSION_DARK_LUMA = 35; // mean below → frame is black(ish)
export const OCCLUSION_FLAT_STDDEV = 10; // stddev below → frame is featureless…
export const OCCLUSION_FLAT_MAX_LUMA = 120; // …but only occluded if also not bright
// Mean |∇luma| below this → no detail anywhere (sharpness proxy). A defocused
// finger pressed on the lens flattens gradients to sensor-noise level.
export const OCCLUSION_MIN_GRADIENT = 2.5;
// Partial occlusion runs on a 4×4 tile grid; a contiguous border-touching
// region of at least this many "blank" tiles (while the rest looks normal)
// means a finger edges into the frame. On-device test 2026-07-20: a finger
// edge over the lens blanks exactly ONE tile (luma 115/σ 72/∇ 4.8 globally,
// blank 1/16) and already degrades the capture, so a single border tile
// warns. Raise this only if real capture floors (dark/uniform around the
// sheet) produce persistent false blanks — check via the diag tile map.
export const OCCLUSION_TILE_GRID = 4;
export const OCCLUSION_TILE_MIN = 1;
// Consecutive occluded frames before the banner fires (~0.5 s at the loop's
// ~9 fps) — a hand passing through the frame must not flash the warning.
export const OCCLUSION_MIN_FRAMES = 5;

// ───────────────────────────────────────────────────────────────────────────
// float32 ⇆ float16 (IEEE-754 half). The model's input tensor is float16 and
// its output may be too; onnxruntime-web hands float16 data back as a
// Uint16Array of raw half-bits. Ported verbatim from the prototype's `ue`/`de`.

/**
 * Pack a float32 array into IEEE-754 half-precision bits (Uint16Array).
 * Pass `out` (same length) to write into a reused buffer and avoid a per-call
 * allocation — the live-guidance loop does this to cut GC churn.
 */
export function float32ToFloat16(src: Float32Array, out?: Uint16Array): Uint16Array {
  out = out && out.length === src.length ? out : new Uint16Array(src.length);
  const buf = new ArrayBuffer(4);
  const f32 = new Float32Array(buf);
  const u32 = new Uint32Array(buf);
  for (let i = 0; i < src.length; i++) {
    f32[0] = src[i];
    const bits = u32[0];
    const sign = (bits >>> 16) & 0x8000;
    let exp = (bits >>> 23) & 0xff;
    let mant = bits & 0x7fffff;
    if (exp === 255) {
      out[i] = sign | 0x7c00 | (mant ? 1 : 0); // Inf / NaN
    } else if (exp === 0) {
      out[i] = sign; // signed zero (subnormals flush to 0)
    } else {
      exp -= 127;
      if (exp > 15) {
        out[i] = sign | 0x7c00; // overflow → Inf
      } else if (exp < -14) {
        mant |= 0x800000;
        const shift = -14 - exp;
        out[i] = shift > 24 ? sign : sign | (mant >>> shift); // subnormal half
      } else {
        out[i] = sign | ((exp + 15) << 10) | (mant >>> 13); // normal half
      }
    }
  }
  return out;
}

/**
 * Decode IEEE-754 half bits (Uint16Array) back to float32. Pass `out` (same
 * length) to reuse a buffer instead of allocating (see `float32ToFloat16`).
 */
export function float16ToFloat32(src: Uint16Array, out?: Float32Array): Float32Array {
  out = out && out.length === src.length ? out : new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) {
    const h = src[i];
    const sign = h & 0x8000 ? -1 : 1;
    const exp = (h & 0x7c00) >> 10;
    const frac = h & 0x03ff;
    if (exp === 0) {
      out[i] = sign * frac * 2 ** -24; // subnormal: frac/1024 * 2^-14
    } else if (exp === 31) {
      out[i] = frac ? NaN : sign * Infinity;
    } else {
      out[i] = sign * (1 + frac / 1024) * 2 ** (exp - 15);
    }
  }
  return out;
}

/**
 * Turn a 128×128 RGBA `ImageData.data` buffer into the model's float16 input
 * buffer: `(channel/255 - mean) / std`, RGB, NCHW planar `[1,3,128,128]`.
 *
 * The optional `scratch` buffers (a `3*PIXELS` float32 plane and a matching
 * float16 output) let the per-frame loop reuse allocations instead of churning
 * ~300 KB of typed arrays every frame.
 */
export function preprocessToFloat16(
  rgba: Uint8ClampedArray,
  planarScratch?: Float32Array,
  halfScratch?: Uint16Array,
): Uint16Array {
  const planar =
    planarScratch && planarScratch.length === 3 * PIXELS
      ? planarScratch
      : new Float32Array(3 * PIXELS);
  for (let p = 0; p < PIXELS; p++) {
    const o = p * 4;
    planar[p] = (rgba[o] / 255 - IMAGENET_MEAN[0]) / IMAGENET_STD[0];
    planar[p + PIXELS] = (rgba[o + 1] / 255 - IMAGENET_MEAN[1]) / IMAGENET_STD[1];
    planar[p + 2 * PIXELS] = (rgba[o + 2] / 255 - IMAGENET_MEAN[2]) / IMAGENET_STD[2];
  }
  return float32ToFloat16(planar, halfScratch);
}

// ───────────────────────────────────────────────────────────────────────────
// Mask postprocessing.

/**
 * Per-pixel argmax over the 4 class planes of `[1,4,128,128]` logits. A pixel
 * only takes its winning class if that logit is > 0, otherwise it's background
 * (prototype threshold).
 */
export function argmaxClassMap(logits: Float32Array): Int8Array {
  const map = new Int8Array(PIXELS);
  for (let p = 0; p < PIXELS; p++) {
    let best = -1e5;
    let cls = 0;
    for (let c = 0; c < NUM_CLASSES; c++) {
      const v = logits[c * PIXELS + p];
      if (v > best) {
        best = v;
        cls = c;
      }
    }
    map[p] = best > 0 ? cls : 0;
  }
  return map;
}

/**
 * Decide whether the wrong foot is in frame for the step we're on, then
 * collapse the two foot classes (1,2) into `CLASS_FOOT` so later geometry is
 * side-agnostic. Mutates `map` in place (the collapse). For a "left" step the
 * right-foot class dominating → wrong foot, and vice-versa (prototype logic).
 */
export function detectWrongFootAndCollapse(map: Int8Array, expectedSide: Side): boolean {
  let leftCount = 0;
  let rightCount = 0;
  for (let p = 0; p < map.length; p++) {
    if (map[p] === CLASS_FOOT) leftCount++;
    else if (map[p] === CLASS_FOOT_OTHER) rightCount++;
  }
  const expectLeft = expectedSide === "left";
  const wrong = expectLeft ? rightCount > leftCount : leftCount > rightCount;
  for (let p = 0; p < map.length; p++) {
    if (map[p] === CLASS_FOOT_OTHER) map[p] = CLASS_FOOT;
  }
  return wrong;
}

/** What `keepLargestComponent` removed — a speckle/confusion proxy (subtask 2b). */
export interface ComponentStats {
  /** Pixels retained (the largest 4-connected component of the class). */
  kept: number;
  /** Pixels zeroed (every smaller component of the class summed). */
  discarded: number;
  /** Distinct components the class had before cleanup (0 = class absent). */
  components: number;
}

/**
 * Keep only the largest 4-connected component of class `cls`; zero the rest.
 * Removes speckle so the bbox tracks the real foot / sheet (prototype `u`).
 * Returns what it dropped: a busy floor makes the model hallucinate scattered
 * paper/foot blobs, so a high discarded-pixel / component count is a substrate-
 * interference signal (`analyzeSubstrate`).
 */
export function keepLargestComponent(map: Int8Array, cls: number): ComponentStats {
  const visited = new Uint8Array(map.length);
  let best: number[] = [];
  let components = 0;
  let total = 0;
  for (let start = 0; start < map.length; start++) {
    if (map[start] !== cls || visited[start]) continue;
    const stack = [start];
    visited[start] = 1;
    const comp: number[] = [];
    while (stack.length) {
      const p = stack.pop() as number;
      comp.push(p);
      const x = p % MODEL_SIZE;
      const y = (p / MODEL_SIZE) | 0;
      if (x > 0 && map[p - 1] === cls && !visited[p - 1]) (visited[p - 1] = 1), stack.push(p - 1);
      if (x < MODEL_SIZE - 1 && map[p + 1] === cls && !visited[p + 1])
        (visited[p + 1] = 1), stack.push(p + 1);
      if (y > 0 && map[p - MODEL_SIZE] === cls && !visited[p - MODEL_SIZE])
        (visited[p - MODEL_SIZE] = 1), stack.push(p - MODEL_SIZE);
      if (y < MODEL_SIZE - 1 && map[p + MODEL_SIZE] === cls && !visited[p + MODEL_SIZE])
        (visited[p + MODEL_SIZE] = 1), stack.push(p + MODEL_SIZE);
    }
    components++;
    total += comp.length;
    if (comp.length > best.length) best = comp;
  }
  const keep = new Uint8Array(map.length);
  for (const p of best) keep[p] = 1;
  for (let p = 0; p < map.length; p++) {
    if (map[p] === cls && !keep[p]) map[p] = 0;
  }
  return { kept: best.length, discarded: total - best.length, components };
}

export interface Bbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Tight bounding box of class `cls` in the 128-space map, or null if absent. */
export function boundingBox(map: Int8Array, cls: number): Bbox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -1;
  let maxY = -1;
  for (let p = 0; p < map.length; p++) {
    if (map[p] !== cls) continue;
    const x = p % MODEL_SIZE;
    const y = (p / MODEL_SIZE) | 0;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Boundary pixels of class `cls` (used to stroke the overlay outline). */
export function edgePixels(map: Int8Array, cls: number): Array<{ x: number; y: number }> {
  const out: Array<{ x: number; y: number }> = [];
  for (let p = 0; p < map.length; p++) {
    if (map[p] !== cls) continue;
    const x = p % MODEL_SIZE;
    const y = (p / MODEL_SIZE) | 0;
    const isEdge =
      x === 0 ||
      y === 0 ||
      x === MODEL_SIZE - 1 ||
      y === MODEL_SIZE - 1 ||
      map[p - 1] !== cls ||
      map[p + 1] !== cls ||
      map[p - MODEL_SIZE] !== cls ||
      map[p + MODEL_SIZE] !== cls;
    if (isEdge) out.push({ x, y });
  }
  return out;
}

// ───────────────────────────────────────────────────────────────────────────
// Lens-occlusion detection. Pure pixel statistics on the same 128×128 RGBA
// frame the model sees — no ONNX involved, so a covered/smeared lens is caught
// even though the segmentation output for such a frame is garbage.

export interface LumaStats {
  /** Mean Rec.601 luma of the frame (0–255). */
  mean: number;
  /** Standard deviation of the luma (0 = perfectly uniform frame). */
  stddev: number;
}

/** Mean + stddev of the Rec.601 luma over an RGBA pixel buffer. */
export function lumaStats(rgba: Uint8ClampedArray): LumaStats {
  const n = rgba.length / 4;
  let sum = 0;
  let sumSq = 0;
  for (let o = 0; o < rgba.length; o += 4) {
    const y = 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
    sum += y;
    sumSq += y * y;
  }
  const mean = sum / n;
  const variance = Math.max(0, sumSq / n - mean * mean);
  return { mean, stddev: Math.sqrt(variance) };
}

/**
 * Single-frame global occlusion verdict: the frame is near-black (finger
 * pressed on the lens / phone flat on a surface), OR the luma variance has
 * collapsed AND the gradient energy is ~0 AND the frame isn't bright — the
 * luma guard keeps a white wall or a sheet filling the frame from triggering.
 */
export function isFrameOccluded(stats: LumaStats, gradient: number): boolean {
  if (stats.mean < OCCLUSION_DARK_LUMA) return true;
  return (
    stats.stddev < OCCLUSION_FLAT_STDDEV &&
    gradient < OCCLUSION_MIN_GRADIENT &&
    stats.mean < OCCLUSION_FLAT_MAX_LUMA
  );
}

export interface OcclusionAnalysis {
  /** Global luma stats of the frame (0–255). */
  mean: number;
  stddev: number;
  /** Mean |∇luma| over within-tile neighbour pairs (sharpness proxy). */
  gradient: number;
  /** Row-major 4×4 map: tile looks covered (dark or contrast+gradient ≈ 0). */
  blankTiles: boolean[];
  /** Whole frame reads as covered. */
  globalOccluded: boolean;
  /** Contiguous border region of blank tiles while the rest looks normal. */
  partialOccluded: boolean;
  /** Single-frame verdict: global OR partial. Feed this into the streak. */
  occluded: boolean;
}

/**
 * Full per-frame occlusion analysis on the 128×128 RGBA frame: global luma
 * mean/stddev + gradient energy, plus a 4×4 tile grid for partial occlusion
 * (a finger edging into the frame covers a connected border region while the
 * rest of the scene stays textured). Gradient pairs never straddle a tile
 * boundary so one hard scene edge can't blank-wash a neighbouring tile.
 */
export function analyzeOcclusion(rgba: Uint8ClampedArray): OcclusionAnalysis {
  const T = OCCLUSION_TILE_GRID;
  const TS = MODEL_SIZE / T; // 32px tiles
  const tiles = T * T;
  const luma = new Float32Array(PIXELS);
  for (let p = 0; p < PIXELS; p++) {
    const o = p * 4;
    luma[p] = 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
  }

  const sum = new Float64Array(tiles);
  const sumSq = new Float64Array(tiles);
  const gradSum = new Float64Array(tiles);
  const gradCnt = new Float64Array(tiles);
  for (let y = 0; y < MODEL_SIZE; y++) {
    const ty = (y / TS) | 0;
    const lastRowInTile = y % TS === TS - 1;
    for (let x = 0; x < MODEL_SIZE; x++) {
      const p = y * MODEL_SIZE + x;
      const t = ty * T + ((x / TS) | 0);
      const v = luma[p];
      sum[t] += v;
      sumSq[t] += v * v;
      if (x % TS !== TS - 1) {
        gradSum[t] += Math.abs(luma[p + 1] - v);
        gradCnt[t]++;
      }
      if (!lastRowInTile) {
        gradSum[t] += Math.abs(luma[p + MODEL_SIZE] - v);
        gradCnt[t]++;
      }
    }
  }

  const tilePixels = TS * TS;
  const blankTiles: boolean[] = new Array(tiles);
  let gSum = 0;
  let gSumSq = 0;
  let gGradSum = 0;
  let gGradCnt = 0;
  for (let t = 0; t < tiles; t++) {
    gSum += sum[t];
    gSumSq += sumSq[t];
    gGradSum += gradSum[t];
    gGradCnt += gradCnt[t];
    const mean = sum[t] / tilePixels;
    const variance = Math.max(0, sumSq[t] / tilePixels - mean * mean);
    blankTiles[t] = isFrameOccluded(
      { mean, stddev: Math.sqrt(variance) },
      gradSum[t] / gradCnt[t],
    );
  }
  const mean = gSum / PIXELS;
  const variance = Math.max(0, gSumSq / PIXELS - mean * mean);
  const stddev = Math.sqrt(variance);
  const gradient = gGradSum / gGradCnt;

  // Largest 4-connected component of blank tiles that touches the grid border
  // (a finger always enters from an edge — a blank centre is scene content).
  let largestBorderComponent = 0;
  let blankCount = 0;
  const visited = new Uint8Array(tiles);
  for (let start = 0; start < tiles; start++) {
    if (blankTiles[start]) blankCount++;
    if (!blankTiles[start] || visited[start]) continue;
    const stack = [start];
    visited[start] = 1;
    let size = 0;
    let touchesBorder = false;
    while (stack.length) {
      const t = stack.pop() as number;
      size++;
      const tx = t % T;
      const ty = (t / T) | 0;
      if (tx === 0 || ty === 0 || tx === T - 1 || ty === T - 1) touchesBorder = true;
      for (const n of [t - 1, t + 1, t - T, t + T]) {
        if (n < 0 || n >= tiles || visited[n] || !blankTiles[n]) continue;
        if ((n === t - 1 || n === t + 1) && ((n / T) | 0) !== ty) continue; // no row wrap
        visited[n] = 1;
        stack.push(n);
      }
    }
    if (touchesBorder && size > largestBorderComponent) largestBorderComponent = size;
  }

  const globalOccluded = isFrameOccluded({ mean, stddev }, gradient);
  // "Rest normal": at least a quarter of the tiles must look like real scene,
  // otherwise the global verdict is the honest signal.
  const partialOccluded =
    largestBorderComponent >= OCCLUSION_TILE_MIN && tiles - blankCount >= T;

  return {
    mean,
    stddev,
    gradient,
    blankTiles,
    globalOccluded,
    partialOccluded,
    occluded: globalOccluded || partialOccluded,
  };
}

/**
 * The verdict that is allowed to LOCK the shutter, taken from a full analysis.
 *
 * Only the global one: a frame that is dark or featureless everywhere is a
 * covered lens and nothing else. The partial (border-tile) branch stays in the
 * analysis for diagnostics but no longer blocks, because it fires on one blank
 * tile out of sixteen — and a dark or low-contrast patch of floor in the corner
 * of the square crop is a normal capture scene, not a finger. On a Redmi Note
 * 11 that misfired constantly and, since the lens banner outranks everything in
 * `guidanceMessage`, it left the customer unable to shoot a perfectly framed
 * sheet. A false lock is far more expensive than a missed partial cover: with
 * fingertips in the corner the sheet-corner solve still fails visibly, and the
 * segmentation guidance says so on its own.
 */
export function lensBlockingVerdict(analysis: OcclusionAnalysis): boolean {
  return analysis.globalOccluded;
}

/**
 * Fold one frame's verdict into the consecutive-occluded-frames counter
 * (resets on any clear frame). The caller owns the counter between frames.
 */
export function occlusionStreak(prev: number, occludedNow: boolean): number {
  return occludedNow ? prev + 1 : 0;
}

/** True once the streak has persisted long enough to warn (no flicker). */
export function isLensBlocked(streak: number): boolean {
  return streak >= OCCLUSION_MIN_FRAMES;
}

// ───────────────────────────────────────────────────────────────────────────
// Paper-shadow detection. Luma statistics restricted to the paper class of the
// segmentation map (pixel-aligned with the same 128×128 RGBA frame). A hard
// directional shadow across the sheet splits the paper luma into a bright, lit
// mode and a darker, shadowed mode — that unevenness confuses the downstream
// sheet-corner solve, so we ADVISE (never block — see `guidanceAdvisory`) the
// customer to even out the lighting. Exposure-invariant by design: the shadow
// test is relative to the paper's own bright level, so a uniformly dim sheet
// (evenly under-exposed, no shadow) does NOT trip it — only real unevenness.

// Below this many visible paper pixels the mask is too small to judge lighting
// reliably (foot occludes much of the sheet, or the paper is far away) → skip.
export const SHADOW_MIN_PAPER_PIXELS = 400; // ~2.4% of the 16 384-px frame
// Luma percentile taken as the sheet's "lit paper" reference (robust to the
// shadowed minority and to speckle at the very top of the range).
export const SHADOW_BRIGHT_PERCENTILE = 0.9;
// A paper pixel dimmer than brightRef × this fraction counts as "shadowed".
export const SHADOW_REL_THRESHOLD = 0.72;
// Shadowed-paper fraction at/above which we advise evening out the lighting.
// Advisory-only + tuned on-device (diag panel), so a generous starting point.
export const SHADOW_RATIO_WARN = 0.2;
// Consecutive uneven frames before the advisory shows (advisory is non-blocking
// and lower-stakes than occlusion, so debounce a touch longer to avoid churn).
export const SHADOW_MIN_FRAMES = 8;

export interface PaperShadowStats {
  /** Visible paper pixels sampled (0 when below `SHADOW_MIN_PAPER_PIXELS`). */
  paperPixels: number;
  /** Mean Rec.601 luma over the paper mask (0–255). */
  mean: number;
  /** Luma stddev over the paper mask (0 = perfectly even sheet). */
  stddev: number;
  /** "Lit paper" reference luma (the `SHADOW_BRIGHT_PERCENTILE` percentile). */
  bright: number;
  /** Fraction of paper pixels dimmer than `bright × SHADOW_REL_THRESHOLD`. */
  shadowRatio: number;
  /** `shadowRatio ≥ SHADOW_RATIO_WARN` → lighting is uneven enough to advise. */
  uneven: boolean;
}

/** The `q`-quantile luma (0–255) from a 256-bin luma histogram of `n` samples. */
function percentileFromHist(hist: Int32Array, n: number, q: number): number {
  const target = q * n;
  let cum = 0;
  for (let b = 0; b < 256; b++) {
    cum += hist[b];
    if (cum >= target) return b;
  }
  return 255;
}

/**
 * Luma unevenness of the paper inside the class map, or null when too little
 * paper is visible to judge. Builds a 256-bin luma histogram over paper pixels,
 * takes a high-percentile "lit paper" reference, and reports what fraction of
 * the sheet sits below a relative shadow threshold. Pure + frame/mask only, so
 * it unit-tests with a synthetic RGBA frame + Int8Array mask.
 */
export function analyzePaperShadow(
  rgba: Uint8ClampedArray,
  map: Int8Array,
): PaperShadowStats | null {
  const hist = new Int32Array(256);
  let n = 0;
  let sum = 0;
  let sumSq = 0;
  for (let p = 0; p < map.length; p++) {
    if (map[p] !== CLASS_PAPER) continue;
    const o = p * 4;
    const y = 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
    let yi = Math.round(y);
    if (yi < 0) yi = 0;
    else if (yi > 255) yi = 255;
    hist[yi]++;
    n++;
    sum += y;
    sumSq += y * y;
  }
  if (n < SHADOW_MIN_PAPER_PIXELS) return null;
  const mean = sum / n;
  const stddev = Math.sqrt(Math.max(0, sumSq / n - mean * mean));
  const bright = percentileFromHist(hist, n, SHADOW_BRIGHT_PERCENTILE);
  const thresh = bright * SHADOW_REL_THRESHOLD;
  let dark = 0;
  for (let b = 0; b < 256 && b < thresh; b++) dark += hist[b];
  const shadowRatio = dark / n;
  return {
    paperPixels: n,
    mean,
    stddev,
    bright,
    shadowRatio,
    uneven: shadowRatio >= SHADOW_RATIO_WARN,
  };
}

/** True once the uneven-lighting streak has persisted long enough to advise. */
export function isShadowAdvised(streak: number): boolean {
  return streak >= SHADOW_MIN_FRAMES;
}

// ───────────────────────────────────────────────────────────────────────────
// Substrate (floor) interference detection. The sheet is meant to sit on a
// surface the model can cleanly separate it from. Two floor conditions degrade
// the paper/foot segmentation without the customer realising the FLOOR is the
// cause:
//
//   • Low contrast — a white sheet on light tiles / a pale desk: the paper's
//     outer edge barely differs from the floor, so the sheet-corner solve drifts.
//   • Busy surface — tile grout, floorboard gaps, patterned rugs: the model
//     hallucinates paper/foot speckle in the pattern and the sheet mask frays.
//
// Both are ADVISORY only (see `guidanceAdvisory`): a plain wood floor with dark
// board gaps demonstrably scans fine, so we never block — we only nudge when the
// interference is measurable AND persistent. Every signal below is pure JS over
// the SAME buffers each frame already has (the 128² RGBA frame + the cleaned
// class map + the component stats from `keepLargestComponent`), so nothing new
// is inferred and the per-frame cost is negligible.

// Width (px, 128-space) of the band sampled either side of the paper↔floor edge
// for the contrast test.
export const SUBSTRATE_BAND_WIDTH = 3;
// Need at least this many band pixels (inner + outer) before the contrast
// verdict is trustworthy — a sliver of visible sheet edge is too noisy to judge.
export const SUBSTRATE_MIN_EDGE_SAMPLES = 200;
// Mean |innerLuma − outerLuma| below this (0–255) = the sheet blends into the
// floor. Advisory-only + tuned on-device (diag panel), so a generous start.
export const SUBSTRATE_CONTRAST_WARN = 18;
// "Busy surface" is a 2-of-5 vote across the signals below, so a single marginal
// metric can't trip the advisory. Each threshold is a generous starting point.
export const SUBSTRATE_DISCARD_WARN = 0.25; // ≥25% of paper pixels were speckle
export const SUBSTRATE_COMPONENTS_WARN = 4; // ≥4 distinct paper blobs pre-cleanup
export const SUBSTRATE_ROUGHNESS_WARN = 2.5; // boundary ≥2.5× the bbox perimeter
export const SUBSTRATE_SOLIDITY_WARN = 0.35; // paper fills ≤35% of its bbox
export const SUBSTRATE_JITTER_WARN = 0.15; // paper box jumps ≥15%/frame
export const SUBSTRATE_VOTES_WARN = 2;
// Consecutive interfering frames before the advisory shows. Non-blocking and
// lower-stakes than occlusion, so debounce a touch longer to stay calm.
export const SUBSTRATE_MIN_FRAMES = 10;
// Frames retained for the diag panel's rolling summary. At the loop's ~9 fps
// this is ~10 s of floor — long enough for a stable p50 while still reacting
// when the sheet is re-placed. Calibration-only: no advisory reads this window.
export const SUBSTRATE_WINDOW_FRAMES = 90;

/** Rec.601 luma (0–255) of the RGBA pixel at flat index `p`. */
function lumaAt(rgba: Uint8ClampedArray, p: number): number {
  const o = p * 4;
  return 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
}

export interface EdgeContrastStats {
  /** Mean luma of the inner band (paper side of the paper↔background edge). */
  inner: number;
  /** Mean luma of the outer band (background/floor side of that edge). */
  outer: number;
  /** |inner − outer|; low → the sheet barely stands out from the floor. */
  contrast: number;
  /** Band pixels sampled (0 unless BOTH sides of the edge were present). */
  samples: number;
}

/**
 * Mean luma just inside vs. just outside the paper↔background boundary, within a
 * `band`-px window. Foot pixels are ignored entirely, so this isolates the
 * sheet's OUTER edge against the floor (not the foot resting on the sheet). Only
 * pixels near the paper bbox are scanned. `samples` is 0 unless both sides were
 * seen, so a sheet framed edge-to-edge (no visible floor) yields no verdict.
 */
export function edgeBandContrast(
  rgba: Uint8ClampedArray,
  map: Int8Array,
  paperBox: Bbox | null,
  band = SUBSTRATE_BAND_WIDTH,
): EdgeContrastStats {
  if (!paperBox) return { inner: 0, outer: 0, contrast: 0, samples: 0 };
  const x0 = Math.max(0, paperBox.x - band);
  const y0 = Math.max(0, paperBox.y - band);
  const x1 = Math.min(MODEL_SIZE - 1, paperBox.x + paperBox.w - 1 + band);
  const y1 = Math.min(MODEL_SIZE - 1, paperBox.y + paperBox.h - 1 + band);
  let inSum = 0;
  let inN = 0;
  let outSum = 0;
  let outN = 0;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const p = y * MODEL_SIZE + x;
      const c = map[p];
      if (c !== CLASS_PAPER && c !== 0) continue; // skip foot pixels
      const want = c === CLASS_PAPER ? 0 : CLASS_PAPER;
      let hit = false;
      for (let dy = -band; dy <= band && !hit; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= MODEL_SIZE) continue;
        for (let dx = -band; dx <= band; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= MODEL_SIZE) continue;
          if (map[yy * MODEL_SIZE + xx] === want) {
            hit = true;
            break;
          }
        }
      }
      if (!hit) continue;
      const l = lumaAt(rgba, p);
      if (c === CLASS_PAPER) {
        inSum += l;
        inN++;
      } else {
        outSum += l;
        outN++;
      }
    }
  }
  const inner = inN ? inSum / inN : 0;
  const outer = outN ? outSum / outN : 0;
  const bothSides = inN > 0 && outN > 0;
  return {
    inner,
    outer,
    contrast: bothSides ? Math.abs(inner - outer) : 0,
    samples: bothSides ? inN + outN : 0,
  };
}

export interface PaperShapeStats {
  /** area / bbox-area (1 = fills its box; a clean angled quad ≈ 0.5–0.8). */
  solidity: number;
  /** boundary length / bbox perimeter (≈1 = clean rect; ≫1 = fringed/jagged). */
  roughness: number;
}

/**
 * Shape health of the kept paper mask: how fully it fills its bbox (solidity)
 * and how long its boundary is relative to a clean rectangle (roughness). Floor
 * pattern bleeding into the paper mask frays the outline — that shows up as a
 * long, jagged boundary well before the bbox itself looks wrong.
 */
export function paperShapeStats(map: Int8Array, paperBox: Bbox | null): PaperShapeStats {
  if (!paperBox) return { solidity: 0, roughness: 0 };
  const { x, y, w, h } = paperBox;
  let area = 0;
  let boundary = 0;
  for (let yy = y; yy < y + h; yy++) {
    for (let xx = x; xx < x + w; xx++) {
      const p = yy * MODEL_SIZE + xx;
      if (map[p] !== CLASS_PAPER) continue;
      area++;
      const isEdge =
        xx === 0 ||
        yy === 0 ||
        xx === MODEL_SIZE - 1 ||
        yy === MODEL_SIZE - 1 ||
        map[p - 1] !== CLASS_PAPER ||
        map[p + 1] !== CLASS_PAPER ||
        map[p - MODEL_SIZE] !== CLASS_PAPER ||
        map[p + MODEL_SIZE] !== CLASS_PAPER;
      if (isEdge) boundary++;
    }
  }
  const bboxArea = w * h;
  const bboxPerim = 2 * (w + h);
  return {
    solidity: bboxArea ? area / bboxArea : 0,
    roughness: bboxPerim ? boundary / bboxPerim : 0,
  };
}

/**
 * Normalised frame-to-frame paper-box displacement (centre shift + width/height
 * change, divided by the box's own scale). ~0 for a steady sheet; a busy floor
 * that the model keeps re-latching onto makes the box jump. 0 when either box is
 * absent (first frame / paper lost) so it never votes without a reference.
 */
export function boxJitter(prev: Bbox | null, cur: Bbox | null): number {
  if (!prev || !cur) return 0;
  const pcx = prev.x + prev.w / 2;
  const pcy = prev.y + prev.h / 2;
  const ccx = cur.x + cur.w / 2;
  const ccy = cur.y + cur.h / 2;
  const centreShift = Math.hypot(ccx - pcx, ccy - pcy);
  const sizeChange = Math.abs(cur.w - prev.w) + Math.abs(cur.h - prev.h);
  const scale = Math.max(1, (cur.w + cur.h) / 2);
  return (centreShift + sizeChange) / scale;
}

export interface SubstrateStats {
  /** Paper↔floor edge-band luma contrast (low = sheet blends into the floor). */
  edgeContrast: number;
  /** Band pixels sampled (0 → contrast not judged; too little visible edge). */
  edgeSamples: number;
  /** Fraction of paper pixels discarded as non-largest speckle (0–1). */
  discardRatio: number;
  /** Distinct paper components the model produced before cleanup. */
  components: number;
  /** area / bbox-area of the kept paper (1 = fills its box). */
  solidity: number;
  /** boundary length / bbox perimeter (≈1 clean, ≫1 fringed). */
  roughness: number;
  /** Normalised frame-to-frame paper-box displacement (0 = no prior box). */
  jitter: number;
  /** Sheet blends into the floor (low edge contrast) → advise more contrast. */
  lowContrast: boolean;
  /** Busy/patterned floor confuses the mask (2-of-5 vote) → advise plainer floor. */
  busy: boolean;
}

/** Which of the five busy-surface signals voted this frame. */
export interface SubstrateVotes {
  /** Speckle fraction ≥ `SUBSTRATE_DISCARD_WARN`. */
  discard: boolean;
  /** Paper component count ≥ `SUBSTRATE_COMPONENTS_WARN`. */
  components: boolean;
  /** Mask roughness ≥ `SUBSTRATE_ROUGHNESS_WARN`. */
  roughness: boolean;
  /** Mask solidity ≤ `SUBSTRATE_SOLIDITY_WARN` (and non-zero). */
  solidity: boolean;
  /** Box jitter ≥ `SUBSTRATE_JITTER_WARN`. */
  jitter: boolean;
}

/** The five busy-surface signals a set of substrate measurements is judged on. */
export type SubstrateSignals = Pick<
  SubstrateStats,
  "discardRatio" | "components" | "roughness" | "solidity" | "jitter"
>;

/**
 * Evaluate the five busy-surface signals independently. Split out of
 * `analyzeSubstrate` so the diag panel's rolling per-signal fire-rate is scored
 * by the SAME predicates as the live verdict — the calibration readout cannot
 * drift from the behaviour it is being used to tune.
 */
export function substrateVotes(s: SubstrateSignals): SubstrateVotes {
  return {
    discard: s.discardRatio >= SUBSTRATE_DISCARD_WARN,
    components: s.components >= SUBSTRATE_COMPONENTS_WARN,
    roughness: s.roughness >= SUBSTRATE_ROUGHNESS_WARN,
    solidity: s.solidity > 0 && s.solidity <= SUBSTRATE_SOLIDITY_WARN,
    jitter: s.jitter >= SUBSTRATE_JITTER_WARN,
  };
}

/** How many of the five busy-surface signals voted. */
export function countSubstrateVotes(v: SubstrateVotes): number {
  return (
    (v.discard ? 1 : 0) +
    (v.components ? 1 : 0) +
    (v.roughness ? 1 : 0) +
    (v.solidity ? 1 : 0) +
    (v.jitter ? 1 : 0)
  );
}

/**
 * Per-frame substrate-interference verdict from four cheap signals, or null when
 * no framed sheet is visible. `lowContrast` (sheet blends into the floor) and
 * `busy` (a patterned floor the model keeps hallucinating into) are distinct
 * floor conditions with opposite remedies, so they surface as separate advisories.
 * `busy` is a conservative 2-of-5 vote (speckle fraction, component count, mask
 * roughness, low solidity, box jitter) so one marginal metric can't trip it.
 * Pure — the caller owns debouncing across frames and passes `prevPaperBox`.
 */
export function analyzeSubstrate(
  rgba: Uint8ClampedArray,
  map: Int8Array,
  paperBox: Bbox | null,
  compStats: ComponentStats,
  prevPaperBox: Bbox | null,
): SubstrateStats | null {
  if (!paperBox) return null;
  const edge = edgeBandContrast(rgba, map, paperBox);
  const shape = paperShapeStats(map, paperBox);
  const total = compStats.kept + compStats.discarded;
  const discardRatio = total ? compStats.discarded / total : 0;
  const jitter = boxJitter(prevPaperBox, paperBox);

  const lowContrast =
    edge.samples >= SUBSTRATE_MIN_EDGE_SAMPLES && edge.contrast < SUBSTRATE_CONTRAST_WARN;

  const votes = countSubstrateVotes(
    substrateVotes({
      discardRatio,
      components: compStats.components,
      roughness: shape.roughness,
      solidity: shape.solidity,
      jitter,
    }),
  );

  return {
    edgeContrast: edge.contrast,
    edgeSamples: edge.samples,
    discardRatio,
    components: compStats.components,
    solidity: shape.solidity,
    roughness: shape.roughness,
    jitter,
    lowContrast,
    busy: votes >= SUBSTRATE_VOTES_WARN,
  };
}

/** True once a substrate-interference streak has persisted long enough to advise. */
export function isSubstrateAdvised(streak: number): boolean {
  return streak >= SUBSTRATE_MIN_FRAMES;
}

/** Low/median/high spread of one signal across the rolling diag window. */
export interface SubstrateSpread {
  p05: number;
  p50: number;
  p95: number;
}

/**
 * Rolling-window view of the substrate signals, for THRESHOLD CALIBRATION only —
 * no advisory reads it. A single diag screenshot is a single frame, and `jitter`
 * and the speckle fraction bounce hard frame-to-frame, so tuning the constants
 * off instantaneous readings risks anchoring on an outlier. This collapses a few
 * seconds of a given floor into a spread per signal plus the rate at which each
 * signal (and each verdict) actually fired — which is what says whether a floor
 * is marginal or clearly on one side, and which single signal is the noisy one.
 */
export interface SubstrateWindowSummary {
  /** Frames the summary covers. */
  frames: number;
  edgeContrast: SubstrateSpread;
  solidity: SubstrateSpread;
  roughness: SubstrateSpread;
  jitter: SubstrateSpread;
  discardRatio: SubstrateSpread;
  components: SubstrateSpread;
  /** Per-signal fraction of frames (0–1) that signal voted busy. */
  fires: Record<keyof SubstrateVotes, number>;
  /** Fraction of frames (0–1) the per-frame `lowContrast` verdict was true. */
  lowContrastRate: number;
  /** Fraction of frames (0–1) the per-frame `busy` verdict was true. */
  busyRate: number;
}

/** Nearest-rank percentile of an ascending-sorted array (0 when empty). */
function percentile(sorted: readonly number[], q: number): number {
  if (!sorted.length) return 0;
  const i = Math.round(q * (sorted.length - 1));
  return sorted[Math.min(sorted.length - 1, Math.max(0, i))];
}

function spread(values: number[]): SubstrateSpread {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    p05: percentile(sorted, 0.05),
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
  };
}

/**
 * Summarise a window of per-frame substrate stats. Null for an empty window so
 * the diag panel renders a placeholder rather than a row of zeroes that would
 * read as "measured, all clean". Frames whose edge contrast could not be judged
 * (`edgeSamples === 0`) are excluded from the `edgeContrast` spread ONLY — they
 * carry no contrast information, and averaging their 0 in would drag the spread
 * toward a false low-contrast reading.
 */
export function summariseSubstrateWindow(
  samples: readonly SubstrateStats[],
): SubstrateWindowSummary | null {
  if (!samples.length) return null;
  const judged = samples.filter((s) => s.edgeSamples > 0).map((s) => s.edgeContrast);
  const votes = samples.map((s) => substrateVotes(s));
  const rate = (n: number): number => n / samples.length;
  return {
    frames: samples.length,
    edgeContrast: spread(judged),
    solidity: spread(samples.map((s) => s.solidity)),
    roughness: spread(samples.map((s) => s.roughness)),
    jitter: spread(samples.map((s) => s.jitter)),
    discardRatio: spread(samples.map((s) => s.discardRatio)),
    components: spread(samples.map((s) => s.components)),
    fires: {
      discard: rate(votes.filter((v) => v.discard).length),
      components: rate(votes.filter((v) => v.components).length),
      roughness: rate(votes.filter((v) => v.roughness).length),
      solidity: rate(votes.filter((v) => v.solidity).length),
      jitter: rate(votes.filter((v) => v.jitter).length),
    },
    lowContrastRate: rate(samples.filter((s) => s.lowContrast).length),
    busyRate: rate(samples.filter((s) => s.busy).length),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Session-level telemetry. The per-shot summaries above only exist for scans
// that COMPLETED — but a floor bad enough to matter is exactly the one that
// never gets there: low paper↔floor contrast degrades the mask until
// `paperDetected` goes false, `guidanceMessage` returns severity "warn", and
// `shutterEnabled` locks the shutter for the whole session. No shot, no upload,
// no sidecar. The worst surfaces would report nothing at all.
//
// So the session also accumulates over EVERY guidance frame, shot or not: how
// often each banner held (i.e. what blocked the shutter and for how long) plus a
// decimated substrate trail. For an unshootable floor that record IS the
// calibration datum — "the mask never held paper for 4 minutes on these tiles"
// says more than any per-shot spread could.

/** Sample the substrate trail every Nth guidance frame (~1.1 s at ~9 fps). */
export const SESSION_SAMPLE_EVERY = 10;
/** Cap the trail so a long session can't grow unbounded (~11 min of frames). */
export const SESSION_MAX_SAMPLES = 600;

/** Running per-session guidance counters. Mutated in place by the frame loop. */
export interface SessionTelemetry {
  /** Guidance frames analysed this session. */
  frames: number;
  /** Frames whose severity was "warn" — i.e. the shutter was locked. */
  blocked: number;
  /** Frames per guidance banner key: what was shown, and for how long. */
  banners: Record<string, number>;
}

export function newSessionTelemetry(): SessionTelemetry {
  return { frames: 0, blocked: 0, banners: {} };
}

/**
 * Fold one frame's guidance verdict into the session counters. Mutates rather
 * than returning a new object: this runs on every guidance frame, and a fresh
 * record per frame would allocate for no benefit.
 */
export function recordSessionFrame(acc: SessionTelemetry, msg: GuidanceMessage): void {
  acc.frames++;
  if (msg.severity === "warn") acc.blocked++;
  acc.banners[msg.key] = (acc.banners[msg.key] ?? 0) + 1;
}

/** Round to `d` decimals so the telemetry JSON stays readable. */
function round(v: number, d: number): number {
  const f = 10 ** d;
  return Math.round(v * f) / f;
}

/** A spread as a compact `[p05, p50, p95]` triple. */
function spreadJson(s: SubstrateSpread, d: number): [number, number, number] {
  return [round(s.p05, d), round(s.p50, d), round(s.p95, d)];
}

/**
 * Compact per-photo substrate telemetry for the `diag` block of
 * `capture-meta.json` — the same numbers the diag panel shows, recorded at each
 * shutter instead of read off a screenshot. Rides the existing sidecar (whose
 * `diag` key is free-form and ignored by the server), so no contract changes:
 * see HEZO_OPS_DESIGN_v0.8 §8.3, where the SPA's uploaded filenames and their
 * contents are opaque to hezo-ops.
 *
 * Sampling at the shutter is deliberate: between shots the customer is moving
 * and re-framing, which spikes `jitter` for reasons that have nothing to do with
 * the floor. At shutter time the framing is settled, so the numbers describe the
 * SURFACE rather than the customer's hand.
 *
 * The thresholds in force are embedded alongside, so a sidecar recovered from a
 * past build still says which constants produced its verdicts. Undefined when no
 * photo carried a summary — the sidecar then keeps its existing shape.
 */
export function substrateTelemetry(
  shots: readonly { filename: string; summary: SubstrateWindowSummary | null }[],
): Record<string, unknown> | undefined {
  const photos: Record<string, unknown> = {};
  for (const { filename, summary } of shots) {
    if (!summary) continue;
    photos[filename] = summaryJson(summary);
  }
  if (!Object.keys(photos).length) return undefined;
  return {
    windowFrames: SUBSTRATE_WINDOW_FRAMES,
    thresholds: {
      contrast: SUBSTRATE_CONTRAST_WARN,
      minEdgeSamples: SUBSTRATE_MIN_EDGE_SAMPLES,
      discard: SUBSTRATE_DISCARD_WARN,
      components: SUBSTRATE_COMPONENTS_WARN,
      roughness: SUBSTRATE_ROUGHNESS_WARN,
      solidity: SUBSTRATE_SOLIDITY_WARN,
      jitter: SUBSTRATE_JITTER_WARN,
      votes: SUBSTRATE_VOTES_WARN,
      minFrames: SUBSTRATE_MIN_FRAMES,
    },
    photos,
  };
}

/** The rolling-window summary as a compact JSON block (shared by both flushes). */
function summaryJson(s: SubstrateWindowSummary): Record<string, unknown> {
  return {
    frames: s.frames,
    edge: spreadJson(s.edgeContrast, 1),
    sol: spreadJson(s.solidity, 3),
    rgh: spreadJson(s.roughness, 3),
    jit: spreadJson(s.jitter, 3),
    spk: spreadJson(s.discardRatio, 3),
    cmp: spreadJson(s.components, 1),
    fires: {
      discard: round(s.fires.discard, 3),
      components: round(s.fires.components, 3),
      roughness: round(s.fires.roughness, 3),
      solidity: round(s.fires.solidity, 3),
      jitter: round(s.fires.jitter, 3),
    },
    lowContrastRate: round(s.lowContrastRate, 3),
    busyRate: round(s.busyRate, 3),
  };
}

/**
 * Whole-session telemetry: the banner histogram plus a spread over the decimated
 * substrate trail. Unlike `substrateTelemetry` this does NOT depend on a shot
 * ever being taken, which is the point — on a floor the guidance never clears,
 * `banners` says which condition held the shutter and `substrate` says what the
 * floor was doing while it did.
 *
 * `blockedRate` near 1 with `banners["banner.noPaperDetected"]` dominating is the
 * signature of a sheet the model cannot separate from the floor: the case the
 * low-contrast advisory exists for, and the one that produces no scan at all.
 *
 * Undefined when no guidance frame was ever analysed (guidance off/unavailable),
 * so an inert session adds nothing to the sidecar.
 */
export function sessionTelemetry(
  acc: SessionTelemetry,
  trail: readonly SubstrateStats[],
): Record<string, unknown> | undefined {
  if (!acc.frames) return undefined;
  const substrate = summariseSubstrateWindow(trail);
  return {
    frames: acc.frames,
    blocked: acc.blocked,
    blockedRate: round(acc.blocked / acc.frames, 3),
    banners: acc.banners,
    sampleEvery: SESSION_SAMPLE_EVERY,
    substrate: substrate ? summaryJson(substrate) : null,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Frame analysis + guidance strings.

export interface GuidanceState {
  footDetected: boolean;
  paperDetected: boolean;
  wrongFoot: boolean;
  paperTooLarge: boolean;
  paperTooSmall: boolean;
  paperTooCloseToEdge: boolean;
  footBox: Bbox | null;
  paperBox: Bbox | null;
}

export interface FrameAnalysis {
  state: GuidanceState;
  /** Foot outline pixels (128-space) for the overlay stroke. */
  footEdges: Array<{ x: number; y: number }>;
  /** Paper outline pixels (128-space) for the overlay stroke. */
  paperEdges: Array<{ x: number; y: number }>;
  /**
   * Luma unevenness of the paper, when the raw frame was supplied to
   * `analyzeFrame` (the live loop passes it). Null when no frame was given
   * (pure logits tests) or too little paper is visible to judge.
   */
  paperShadow: PaperShadowStats | null;
  /**
   * Substrate (floor) interference stats, when the raw frame was supplied. Null
   * without a frame, or when no framed sheet is visible to judge (subtask 2b).
   */
  substrate: SubstrateStats | null;
}

/**
 * Full per-frame pipeline: logits → class map → wrong-foot → component cleanup
 * → bboxes → guidance booleans + overlay edges. Pass the raw 128×128 RGBA
 * frame to also get the paper-shadow (uneven-lighting) + substrate (floor-
 * interference) stats — both are pixel-aligned with the class map. `prevPaperBox`
 * (the previous frame's paper box) feeds the substrate box-jitter signal; omit
 * it on the first frame. Pure; the caller owns drawing, the deviceorientation
 * angle, and threading `prevPaperBox` across frames.
 */
export function analyzeFrame(
  logits: Float32Array,
  expectedSide: Side,
  rgba?: Uint8ClampedArray,
  prevPaperBox?: Bbox | null,
): FrameAnalysis {
  const map = argmaxClassMap(logits);
  const wrongFoot = detectWrongFootAndCollapse(map, expectedSide);
  const paperComp = keepLargestComponent(map, CLASS_PAPER);
  keepLargestComponent(map, CLASS_FOOT);

  const footBox = boundingBox(map, CLASS_FOOT);
  let paperBox = boundingBox(map, CLASS_PAPER);
  if (paperBox && paperBox.w < MODEL_SIZE * PAPER_MIN_WIDTH_FRAC) paperBox = null;

  const large = MODEL_SIZE * PAPER_TOO_LARGE_FRAC;
  const small = MODEL_SIZE * PAPER_TOO_SMALL_FRAC;
  const margin = MODEL_SIZE * PAPER_EDGE_FRAC;

  const paperTooLarge = !!paperBox && (paperBox.w > large || paperBox.h > large);
  const paperTooSmall = !!paperBox && Math.max(paperBox.w, paperBox.h) < small;
  const paperTooCloseToEdge =
    !!paperBox &&
    (paperBox.x < margin ||
      paperBox.y < margin ||
      paperBox.x + paperBox.w > MODEL_SIZE - margin ||
      paperBox.y + paperBox.h > MODEL_SIZE - margin);

  const state: GuidanceState = {
    footDetected: !!footBox,
    paperDetected: !!paperBox,
    wrongFoot,
    paperTooLarge,
    paperTooSmall,
    paperTooCloseToEdge,
    footBox,
    paperBox,
  };

  return {
    state,
    footEdges: footBox ? edgePixels(map, CLASS_FOOT) : [],
    paperEdges: paperBox ? edgePixels(map, CLASS_PAPER) : [],
    // Shadow + substrate tests run on the cleaned paper mask; both need the raw
    // frame, and substrate also folds in the pre-cleanup paper component stats.
    paperShadow: rgba ? analyzePaperShadow(rgba, map) : null,
    substrate: rgba ? analyzeSubstrate(rgba, map, paperBox, paperComp, prevPaperBox ?? null) : null,
  };
}

/** Angle from the phone's deviceorientation pitch (prototype: `90 - round(beta)`). */
export function angleFromBeta(beta: number): number {
  return 90 - Math.round(beta);
}

/** Reading for `photoIndex`: inside its window, or which way it is off. */
export function angleReading(angle: number, photoIndex: 1 | 2 | 3): AngleReading {
  const [lo, hi] = ANGLE_WINDOWS[photoIndex];
  return { value: angle, good: angle >= lo && angle <= hi, low: angle < lo };
}

// "warn" blocks the shutter (see `shutterEnabled`); "good" is positive feedback;
// "advisory" is a non-blocking hint (uneven lighting, and — later — substrate
// signals) shown in a second banner slot without ever trapping the customer.
export type GuidanceSeverity = "warn" | "good" | "advisory";

/**
 * The banner i18n keys (must match the keys in `lib/i18n/locales/*.json`). Kept as a
 * plain string union so this module stays React/i18n-free and unit-testable;
 * the component resolves the key via `t()`.
 */
export type GuidanceKey =
  | "banner.lensOccluded"
  | "banner.noFootNoPaperDetected"
  | "banner.noPaperDetected"
  | "banner.noFootDetected"
  | "banner.wrongFoot"
  | "banner.cameraLow"
  | "banner.cameraHigh"
  | "banner.paperTooLarge"
  | "banner.paperTooSmall"
  | "banner.paperEdge"
  | "banner.perfectAngle"
  | "banner.readyToCapture"
  | "banner.unevenLighting"
  | "banner.lowPaperContrast"
  | "banner.busySurface";

export interface GuidanceMessage {
  key: GuidanceKey;
  severity: GuidanceSeverity;
}

/**
 * Current tilt reading, or null when the device exposes no orientation (in
 * which case the angle checks are skipped, exactly like the prototype's `L` flag).
 */
export interface AngleReading {
  value: number;
  good: boolean;
  /** Below the photo's window: the camera looks too far ahead, not down enough. */
  low: boolean;
}

/**
 * Map the analysed state (+ optional tilt) to a single banner key, in the
 * prototype's exact priority order. Returns an i18n key + severity; the component
 * resolves it to localised text via `t()` (the prototype's `J()` cascade).
 */
export function guidanceMessage(
  state: GuidanceState,
  angle: AngleReading | null,
  lensBlocked = false,
): GuidanceMessage {
  // Lens occlusion outranks everything: with a covered lens the segmentation
  // state below is meaningless, and "NO FOOT DETECTED" would misdirect the
  // customer at the sheet instead of the lens.
  if (lensBlocked) return { key: "banner.lensOccluded", severity: "warn" };
  if (!state.footDetected && !state.paperDetected)
    return { key: "banner.noFootNoPaperDetected", severity: "warn" };
  if (!state.paperDetected) return { key: "banner.noPaperDetected", severity: "warn" };
  if (!state.footDetected) return { key: "banner.noFootDetected", severity: "warn" };
  if (state.wrongFoot) return { key: "banner.wrongFoot", severity: "warn" };
  if (angle && !angle.good)
    return {
      key: angle.low ? "banner.cameraLow" : "banner.cameraHigh",
      severity: "warn",
    };
  if (state.paperTooLarge) return { key: "banner.paperTooLarge", severity: "warn" };
  if (state.paperTooSmall) return { key: "banner.paperTooSmall", severity: "warn" };
  if (state.paperTooCloseToEdge) return { key: "banner.paperEdge", severity: "warn" };
  if (angle && angle.good) return { key: "banner.perfectAngle", severity: "good" };
  return { key: "banner.readyToCapture", severity: "good" };
}

/**
 * The banner when the AI guidance is off, still loading or unavailable (`?ai=none`,
 * a device that cannot run the model): the tilt alone, from the orientation
 * sensor, so the camera-angle window still gates the shutter. Null without a
 * reading, which leaves the shutter free as before.
 */
export function angleOnlyMessage(angle: AngleReading | null): GuidanceMessage | null {
  if (!angle) return null;
  if (!angle.good)
    return { key: angle.low ? "banner.cameraLow" : "banner.cameraHigh", severity: "warn" };
  return { key: "banner.perfectAngle", severity: "good" };
}

/** Debounced advisory verdicts the caller has decided are worth surfacing. */
export interface AdvisoryFlags {
  /** Uneven paper lighting — a hard shadow across the sheet (subtask 2a). */
  lightingUneven: boolean;
  /** The sheet blends into the floor — too little edge contrast (subtask 2b). */
  lowPaperContrast: boolean;
  /** A busy/patterned floor is confusing the segmentation (subtask 2b). */
  busySurface: boolean;
}

/**
 * The optional advisory banner, shown in a SECOND slot alongside the primary
 * guidance message. Non-blocking by construction — its severity is "advisory",
 * which `shutterEnabled` never treats as a block — so a false positive can only
 * nudge, never lock the customer out. Returns null when nothing to advise.
 *
 * At most one advisory shows at a time, in priority order: lighting first (the
 * established 2a signal), then low paper contrast (the most specific substrate
 * signal), then a generally busy surface. Low-contrast and busy are opposite
 * floor conditions that rarely co-occur; the order just breaks the tie.
 */
export function guidanceAdvisory(flags: AdvisoryFlags): GuidanceMessage | null {
  if (flags.lightingUneven) return { key: "banner.unevenLighting", severity: "advisory" };
  if (flags.lowPaperContrast) return { key: "banner.lowPaperContrast", severity: "advisory" };
  if (flags.busySurface) return { key: "banner.busySurface", severity: "advisory" };
  return null;
}
